import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getCalibration,
  getParameters,
  getScopeItems,
  openCalibration,
  previewCalibration,
  publishCalibration,
  type PreviewRequest,
  type PublishRequest,
} from "@/api/calibration";

export const calibrationKeys = {
  active: (projectId: string) => ["calibration", projectId] as const,
  scopeItems: (projectId: string) => ["calibration", projectId, "scope-items"] as const,
  parameters: (projectId: string) => ["calibration", projectId, "parameters"] as const,
};

/**
 * A calibragem ativa do projeto.
 *
 * 404 é estado normal, e não erro: projeto sem calibragem renderiza o
 * Diagnóstico como sempre renderizou. Por isso `retry: false` — insistir num
 * 404 só atrasa a tela para chegar na mesma resposta.
 */
export function useCalibration(projectId: string | undefined) {
  return useQuery({
    queryKey: calibrationKeys.active(projectId ?? ""),
    queryFn: () => getCalibration(projectId!),
    enabled: !!projectId,
    retry: false,
  });
}

/**
 * A lista da Calibragem, que sai da aprovação de escopo do Functional.
 *
 * Sem cache longo: ela muda quando alguém reabre ou aprova o escopo, e voltar
 * à Calibragem depois disso tem de mostrar a lista nova.
 */
export function useCalibrationScopeItems(projectId: string | undefined) {
  return useQuery({
    queryKey: calibrationKeys.scopeItems(projectId ?? ""),
    queryFn: () => getScopeItems(projectId!),
    enabled: !!projectId,
  });
}

/** Os 55 parâmetros. Mudam por promoção de curadoria, não por uso: cache longo. */
export function useCalibrationParameters(projectId: string | undefined) {
  return useQuery({
    queryKey: calibrationKeys.parameters(projectId ?? ""),
    queryFn: () => getParameters(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });
}

export function useOpenCalibration(projectId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => openCalibration(projectId!),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: calibrationKeys.active(projectId ?? "") }),
  });
}

/**
 * O recálculo ao vivo.
 *
 * `useMutation` e não `useQuery` porque a tela dispara a cada ajuste e o
 * resultado não é cacheável por chave: o corpo é o estado inteiro da tela.
 * **Não grava nada** no servidor; ver `previewCalibration`.
 */
export function useCalibrationPreview(projectId: string | undefined) {
  return useMutation({
    mutationFn: (body: PreviewRequest) => previewCalibration(projectId!, body),
  });
}

export function usePublishCalibration(projectId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PublishRequest) => publishCalibration(projectId!, body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: calibrationKeys.active(projectId ?? "") });
      // O Diagnóstico passa a ler o publicado assim que o worker materializar.
      client.invalidateQueries({ queryKey: ["diagnostic", projectId ?? ""] });
    },
  });
}
